/**
 * API Service pentru sistemul de permisii RBAC
 */

import axios from 'axios'
import { getAccessToken } from '../utils/tokenStorage'

const API_BASE_URL = `${window.location.origin}/api/v1`

// Instanță axios pentru permissions API
const permissionsClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Interceptor pentru autentificare
permissionsClient.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ═══════════════════════════════════════════════════════════════════════════
// TIPURI
// ═══════════════════════════════════════════════════════════════════════════

export interface Permission {
  id: string
  code: string
  resource: string
  action: string
  description?: string
  category?: string
  requires_scope: boolean
  is_system: boolean
  created_at: string
}

export interface PermissionListResponse {
  permissions: Permission[]
  total: number
}

export interface RoleWithPermissions {
  id: string
  name: string
  description?: string
  permission_codes: string[]
}

export interface RoleListResponse {
  roles: RoleWithPermissions[]
  total: number
}

export interface UserPermissions {
  user_id: string
  user_email: string
  user_role: string
  role_permissions: string[]
  granted_overrides: string[]
  revoked_overrides: string[]
  effective_permissions: string[]
}

export interface PermissionOverride {
  id: string
  user_id: string
  permission_code: string
  grant_type: 'grant' | 'revoke'
  expires_at?: string
  reason?: string
  granted_by: string
  created_at: string
  user_email?: string
  granted_by_email?: string
  is_expired: boolean
}

export interface PermissionOverrideCreate {
  user_id: string
  permission_code: string
  grant_type: 'grant' | 'revoke'
  expires_at?: string
  reason?: string
}

export interface PermissionMatrixCell {
  permission_code: string
  has_permission: boolean
  is_inherited: boolean
}

export interface PermissionMatrixRow {
  resource: string
  resource_display: string
  actions: Record<string, PermissionMatrixCell>
}

export interface PermissionMatrix {
  target_type: 'role' | 'user'
  target_id: string
  target_name: string
  rows: PermissionMatrixRow[]
  available_actions: string[]
}

export interface BulkPermissionAssign {
  role_id: string
  permission_codes: string[]
  replace: boolean
}

export interface PermissionCheckResult {
  permission_code: string
  has_permission: boolean
  user_role: string
}

// ═══════════════════════════════════════════════════════════════════════════
// SERVICIU API
// ═══════════════════════════════════════════════════════════════════════════

export const permissionsService = {
  /**
   * Obține toate permisiunile disponibile
   */
  async listPermissions(category?: string, resource?: string): Promise<PermissionListResponse> {
    const params = new URLSearchParams()
    if (category) params.append('category', category)
    if (resource) params.append('resource', resource)
    
    const response = await permissionsClient.get<PermissionListResponse>(
      `/permissions?${params.toString()}`
    )
    return response.data
  },

  /**
   * Obține permisiunile utilizatorului curent
   */
  async getMyPermissions(): Promise<UserPermissions> {
    const response = await permissionsClient.get<UserPermissions>('/permissions/me')
    return response.data
  },

  /**
   * Obține lista categoriilor de permisiuni
   */
  async getCategories(): Promise<string[]> {
    const response = await permissionsClient.get<string[]>('/permissions/categories')
    return response.data
  },

  /**
   * Obține lista rolurilor cu permisiuni
   */
  async listRoles(): Promise<RoleListResponse> {
    const response = await permissionsClient.get<RoleListResponse>('/permissions/roles')
    return response.data
  },

  /**
   * Obține detaliile unui rol
   */
  async getRole(roleId: string): Promise<RoleWithPermissions> {
    const response = await permissionsClient.get<RoleWithPermissions>(`/permissions/roles/${roleId}`)
    return response.data
  },

  /**
   * Actualizează permisiunile unui rol
   */
  async updateRolePermissions(
    roleId: string, 
    permissionCodes: string[], 
    replace: boolean = true
  ): Promise<{ success: boolean; affected_count: number; message: string }> {
    const response = await permissionsClient.put(`/permissions/roles/${roleId}/permissions`, {
      role_id: roleId,
      permission_codes: permissionCodes,
      replace,
    })
    return response.data
  },

  /**
   * Obține permisiunile unui utilizator specific
   */
  async getUserPermissions(userId: string): Promise<UserPermissions> {
    const response = await permissionsClient.get<UserPermissions>(`/permissions/users/${userId}/permissions`)
    return response.data
  },

  /**
   * Obține override-urile unui utilizator
   */
  async getUserOverrides(userId: string): Promise<{ overrides: PermissionOverride[]; total: number }> {
    const response = await permissionsClient.get(`/permissions/users/${userId}/overrides`)
    return response.data
  },

  /**
   * Creează un override pentru un utilizator
   */
  async createUserOverride(
    userId: string, 
    data: Omit<PermissionOverrideCreate, 'user_id'>
  ): Promise<PermissionOverride> {
    const response = await permissionsClient.post<PermissionOverride>(
      `/permissions/users/${userId}/overrides`,
      { ...data, user_id: userId }
    )
    return response.data
  },

  /**
   * Șterge un override
   */
  async deleteUserOverride(userId: string, overrideId: string): Promise<void> {
    await permissionsClient.delete(`/permissions/users/${userId}/overrides/${overrideId}`)
  },

  /**
   * Obține matricea de permisiuni pentru un rol
   */
  async getRoleMatrix(roleName: string): Promise<PermissionMatrix> {
    const response = await permissionsClient.get<PermissionMatrix>(`/permissions/matrix/role/${roleName}`)
    return response.data
  },

  /**
   * Verifică dacă utilizatorul curent are o permisiune
   */
  async checkPermission(permissionCode: string): Promise<PermissionCheckResult> {
    const response = await permissionsClient.get<PermissionCheckResult>(`/permissions/check/${permissionCode}`)
    return response.data
  },

  /**
   * Verifică mai multe permisiuni simultan
   */
  async checkPermissionsBulk(permissionCodes: string[]): Promise<{ permissions: Record<string, boolean>; user_role: string }> {
    const response = await permissionsClient.post('/permissions/check/bulk', permissionCodes)
    return response.data
  },
}

export default permissionsService

